import { useState, useEffect, useCallback } from "react";
import handleService from "../services/apiService.ts";

export interface PlanLimitsData {
  planoNome: string | null;
  limiteUsuarios: number;
  notificarEmail: boolean;
  integracaoApi: boolean;
  downgradeAgendado: boolean;
  planoFuturoNome: string | null;
  // Configuração da empresa (Configurações > Empresa), não gate de plano —
  // viaja neste DTO só porque o form de nova cirurgia já consome /plano/limites.
  maxInstrumentadores: number;
}

const TTL_MS = 5 * 60 * 1000;
const TIMEOUT_MS = 20_000;

// Cache em escopo de modulo, mesmo molde do useCirurgiaOpcoes. Antes daqui,
// este hook refazia GET /plano/limites a CADA abertura do drawer de Nova
// Cirurgia (o Drawer desmonta o form ao fechar), somando uma 7a requisicao
// concorrente as 6 listas de opcoes — justamente o que estoura o pool de
// conexoes do backend e trava a tela.
let cache: { data: PlanLimitsData | null; timestamp: number } | null = null;
let inflight: Promise<PlanLimitsData | null> | null = null;

const cacheValido = () => !!cache && Date.now() - cache.timestamp < TTL_MS;

const buscar = async (): Promise<PlanLimitsData | null> => {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await handleService({ endpoint: "/plano/limites", timeout: TIMEOUT_MS });
      const data: PlanLimitsData | null = res?.data || null;
      cache = { data, timestamp: Date.now() };
      return data;
    } catch {
      // Falha nao entra no cache: a proxima abertura tenta de novo em vez de
      // ficar 5 minutos achando que o plano nao tem limite nenhum.
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

/** Invalida o cache — chamar quando o plano da empresa mudar (troca, reativacao). */
export const invalidarPlanLimitsCache = () => {
  cache = null;
};

export const usePlanLimits = () => {
  const [limits, setLimits] = useState<PlanLimitsData | null>(cache?.data ?? null);
  const [isLoading, setIsLoading] = useState(!cacheValido());

  const fetchLimits = useCallback(async (forcar = false) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
      return;
    }

    if (!forcar && cacheValido()) {
      setLimits(cache!.data);
      setIsLoading(false);
      return;
    }

    if (forcar) cache = null;
    setIsLoading(true);
    setLimits(await buscar());
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  return { limits, isLoading, refreshLimits: () => fetchLimits(true) };
};
